import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TablePagination,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import {
  Coupon,
  CouponDisplayStatus,
  CouponType,
  couponService,
} from "../../../api/services/couponService";
import {
  StyledDrawerContent,
  StyledDrawerHeader,
} from "../../../components/DrawerTemplate";
import EmptyScreen from "../../../components/EmptyScreen";
import { DeleteButton, EditButton } from "../../../components/IconButtons";
import Loader from "../../../components/Loader";
import { ModernHeader } from "../../../components/modern";
import { RoleGuard } from "../../../components/RBAC";
import Table from "../../../components/TableComponent";
import { CouponForm } from "./CouponForm";
import {
  ClinicianOption,
  ClinicianSearchAutocomplete,
} from "./ClinicianSearchAutocomplete";
import {
  PatientOption,
  PatientSearchAutocomplete,
} from "./PatientSearchAutocomplete";

const STATUS_CHIP: Record<
  CouponDisplayStatus,
  { label: string; color: "success" | "default" | "error" | "warning" }
> = {
  ACTIVE: { label: "Active", color: "success" },
  EXPIRED: { label: "Expired", color: "error" },
  INACTIVE: { label: "Inactive", color: "default" },
  SCHEDULED: { label: "Scheduled", color: "warning" },
};

const SCOPE_LABEL: Record<string, string> = {
  ALL: "All",
  SERVICE: "Services",
  CLINICIAN: "Clinicians",
};

export const Coupons = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<CouponType | "">("");
  const [activeFilter, setActiveFilter] = useState<"" | "true" | "false">("");
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(0);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState<Coupon | null>(null);
  const [grantOpen, setGrantOpen] = useState(false);
  const [grantPatient, setGrantPatient] = useState<PatientOption | null>(null);
  const [grantClinicians, setGrantClinicians] = useState<ClinicianOption[]>(
    []
  );
  const [grantCount, setGrantCount] = useState("3");
  const [grantTitle, setGrantTitle] = useState("Free sessions");

  const { data, isLoading } = useQuery(
    [
      "coupons",
      {
        search,
        limit,
        offset: limit * page,
        type: typeFilter || undefined,
        is_active:
          activeFilter === "" ? undefined : activeFilter === "true",
      },
    ],
    ({ queryKey }) =>
      couponService.list(queryKey[1] as Record<string, unknown>),
    { keepPreviousData: true }
  );

  const coupons: Coupon[] = data?.data?.data || [];
  const total = data?.data?.meta?.total || 0;

  const { mutate: deleteCoupon } = useMutation(
    (id: number) => couponService.remove(id),
    {
      onSuccess: (res: any) => {
        queryClient.invalidateQueries("coupons");
        toast.success(res?.data?.message || "Coupon deleted");
      },
      onError: (res: any) => {
        toast.error(res?.response?.data?.message || "Failed to delete coupon");
      },
    }
  );

  const { mutate: grantFree, isLoading: isGranting } = useMutation(
    () =>
      couponService.grantFreeSessions({
        patientId: grantPatient!.id,
        count: Number(grantCount) || 3,
        title_en: grantTitle.trim() || undefined,
        clinicianIds:
          grantClinicians.length > 0
            ? grantClinicians.map((c) => c.id)
            : undefined,
      }),
    {
      onSuccess: (res: any) => {
        queryClient.invalidateQueries("coupons");
        toast.success(
          res?.data?.message ||
            `Granted ${grantCount} free session(s) to patient ${grantPatient?.id}`
        );
        setGrantOpen(false);
        setGrantPatient(null);
        setGrantClinicians([]);
        setGrantCount("3");
        setGrantTitle("Free sessions");
      },
      onError: (res: any) => {
        toast.error(
          res?.response?.data?.message || "Failed to grant free sessions"
        );
      },
    }
  );

  const handleEditClick = async (row: Coupon) => {
    try {
      const res = await couponService.getById(row.id);
      setFormData(res.data.data);
      setOpen(true);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to load coupon");
    }
  };

  const handleDeleteClick = (id: number) => {
    if (
      window.confirm(
        "Delete this coupon? Coupons with redemptions will be deactivated instead."
      )
    ) {
      deleteCoupon(id);
    }
  };

  const handleAdd = () => {
    setFormData(null);
    setOpen(true);
  };

  const formatDiscount = (row: Coupon) =>
    row.type === "PERCENTAGE" ? `${row.value}%` : `${row.value} SAR`;

  const Columns = [
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      render: (code: string) => (
        <Typography sx={{ fontWeight: 600, fontFamily: "monospace" }}>
          {code}
        </Typography>
      ),
    },
    {
      title: "Title",
      dataIndex: "title_en",
      key: "title_en",
    },
    {
      title: "Discount",
      dataIndex: "value",
      key: "value",
      render: (_: unknown, row: Coupon) => formatDiscount(row),
    },
    {
      title: "Scope",
      dataIndex: "scope",
      key: "scope",
      render: (scope: string, row: Coupon) => {
        const label = SCOPE_LABEL[scope] || scope || "All";
        const detail =
          scope === "CLINICIAN"
            ? ` (${row.clinician_ids?.length || 0})`
            : scope === "SERVICE"
              ? ` (${row.service_ids?.length || 0})`
              : "";
        return (
          <Chip
            label={`${label}${detail}`}
            size="small"
            variant="outlined"
          />
        );
      },
    },
    {
      title: "Status",
      dataIndex: "display_status",
      key: "display_status",
      render: (status: CouponDisplayStatus) => {
        const chip = STATUS_CHIP[status] || STATUS_CHIP.INACTIVE;
        return (
          <Chip label={chip.label} color={chip.color} size="small" />
        );
      },
    },
    {
      title: "Usage",
      dataIndex: "usage_count",
      key: "usage_count",
      render: (count: number, row: Coupon) => (
        <Typography sx={{ fontSize: 14 }}>
          {count ?? 0}
          {row.usage_limit != null ? ` / ${row.usage_limit}` : ""}
        </Typography>
      ),
    },
    {
      title: "Visibility",
      dataIndex: "is_public",
      key: "is_public",
      render: (isPublic: boolean) => (
        <Chip
          label={isPublic ? "Public" : "Private"}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      title: "Actions",
      dataIndex: "id",
      key: "id",
      render: (id: number, row: Coupon) => (
        <Box display="flex" gap="20px" justifyContent="center">
          <EditButton onClick={() => handleEditClick(row)} />
          <RoleGuard allowedRoles={["SUPER_ADMIN"]}>
            <DeleteButton onClick={() => handleDeleteClick(id)} />
          </RoleGuard>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ backgroundColor: "#f5f7fa", minHeight: "100vh", pb: 4 }}>
      <ModernHeader
        title="Coupons"
        subtitle={`Total: ${total} coupons`}
        actions={
          <Box display="flex" gap={1.5}>
            <Button
              variant="outlined"
              startIcon={<CardGiftcardIcon />}
              onClick={() => setGrantOpen(true)}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                px: 2.5,
              }}
            >
              Grant Free Sessions
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                px: 3,
              }}
            >
              Add Coupon
            </Button>
          </Box>
        }
      />

      <Box px={3}>
        <Box
          sx={{
            backgroundColor: "#fff",
            p: 2.5,
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            mb: 3,
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <TextField
            label="Search by code or title"
            placeholder="Enter code or title..."
            size="small"
            sx={{ minWidth: 280, flex: 1, maxWidth: 420 }}
            InputProps={{
              endAdornment: (
                <IconButton size="small">
                  <SearchIcon />
                </IconButton>
              ),
            }}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Type</InputLabel>
            <Select
              label="Type"
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value as CouponType | "");
                setPage(0);
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="PERCENTAGE">Percentage</MenuItem>
              <MenuItem value="FIXED">Fixed</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Active</InputLabel>
            <Select
              label="Active"
              value={activeFilter}
              onChange={(e) => {
                setActiveFilter(e.target.value as "" | "true" | "false");
                setPage(0);
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Active</MenuItem>
              <MenuItem value="false">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {total === 0 && !isLoading ? (
          <EmptyScreen type="coupons" />
        ) : isLoading ? (
          <Loader />
        ) : (
          <Box
            sx={{
              backgroundColor: "#fff",
              borderRadius: "12px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              overflow: "hidden",
            }}
          >
            <Table
              headAlign="center"
              tableAlign="center"
              key="coupons"
              dataSource={coupons}
              rowKey="id"
              columns={Columns}
              loading={isLoading}
            />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderTop: "1px solid rgba(0,0,0,0.08)",
              }}
            >
              <TablePagination
                count={total}
                page={page}
                onPageChange={(_e, newPage) => setPage(newPage)}
                rowsPerPage={limit}
                onRowsPerPageChange={(e) => {
                  setLimit(parseInt(e.target.value, 10));
                  setPage(0);
                }}
              />
            </Box>
          </Box>
        )}
      </Box>

      <Drawer anchor="right" open={open} onClose={() => setOpen(false)}>
        <StyledDrawerHeader>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {formData ? "Edit Coupon" : "Add Coupon"}
          </Typography>
          <Typography
            sx={{
              fontSize: "14px",
              fontFamily: "Inter_semibold",
              color: "black",
              opacity: "0.5",
              cursor: "pointer",
              "&:hover": { opacity: 0.8 },
            }}
            onClick={() => setOpen(false)}
          >
            Cancel
          </Typography>
        </StyledDrawerHeader>
        <StyledDrawerContent>
          <CouponForm
            key={formData?.id ?? "new"}
            formData={formData}
            toggleDrawer={(v: boolean) => setOpen(v)}
          />
        </StyledDrawerContent>
      </Drawer>

      <Dialog
        open={grantOpen}
        onClose={() => !isGranting && setGrantOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Grant Free Sessions</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Creates a private 100% coupon for this patient. Each use waives at most
            one timeslot (multi-slot bookings still charge the rest). At checkout
            they can apply it and book; single-slot bookings are fully free.
          </Typography>
          <PatientSearchAutocomplete
            value={grantPatient}
            onChange={setGrantPatient}
            label="Patient"
            placeholder="Search by name, email, phone, or ID…"
            helperText="Select a patient from search results."
            disabled={isGranting}
          />
          <Box sx={{ mt: 1.5 }}>
            <ClinicianSearchAutocomplete
              value={grantClinicians}
              onChange={setGrantClinicians}
              label="Limit to clinicians (optional)"
              placeholder="Search by name, email, phone, or ID…"
              helperText="Leave empty to allow any clinician. Select specific clinicians to restrict."
              disabled={isGranting}
            />
          </Box>
          <TextField
            fullWidth
            label="Number of free sessions"
            type="number"
            value={grantCount}
            onChange={(e) => setGrantCount(e.target.value)}
            margin="dense"
            inputProps={{ min: 1, max: 50 }}
          />
          <TextField
            fullWidth
            label="Title"
            value={grantTitle}
            onChange={(e) => setGrantTitle(e.target.value)}
            margin="dense"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setGrantOpen(false);
              setGrantPatient(null);
              setGrantClinicians([]);
            }}
            disabled={isGranting}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={isGranting || !grantPatient?.id}
            onClick={() => grantFree()}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {isGranting ? "Granting..." : "Grant"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Coupons;
